import React, { useState, useEffect } from "react";
import {
  startOfDay,
  isWithinInterval,
  endOfDay,
  addDays,
  parse,
  isValid
} from "date-fns";
import { connect } from "react-redux";
import { Switch, Route, useHistory } from "react-router-dom";

import List from "./List";
import DetailsContainer from "./details/DetailsContainer";
import { FORMAT_DATE } from "../../common/dateFormats";
import { twoWeeksDown } from "../../common/dateFunctions";
import { ROLE_SYSTEM_ADMINISTRATOR } from "../../security/role";
import { setLocationFilter, setCategoryFilter } from "../../actions/ui";

function ListContainer({
  dispatch,
  trainingUnits,
  initialDisplayPeriod,
  appliedFilter,
  locations,
  courseCategories,
  onUpdateFilter,
  isLoading,
  onLoadMore,
  onRefreshData,
  myRole
}) {
  const history = useHistory();

  const [displayPeriod, setDisplayPeriod] = useState({
    from: initialDisplayPeriod.from,
    to: initialDisplayPeriod.to
  });

  // Check if the locationFilter from persisted state is a valid locationId
  useEffect(() => {
    console.debug(
      "Check if the locationFilter from persisted state is a valid locationId"
    );
    if (!appliedFilter.locationId) {
      return;
    }

    if (locations.length < 2) {
      return;
    }

    const a = locations.filter(
      location => location.id === appliedFilter.locationId
    );

    if (a.length === 0) {
      dispatch(setLocationFilter(null));
      onUpdateFilter({
        ...appliedFilter,
        locationId: null
      });
    }
  }, [locations, appliedFilter, dispatch, onUpdateFilter]);

  // Check if the categoryFilter from persisted state is a valid course category
  useEffect(() => {
    console.debug(
      "Check if the categoryFilter from persisted state is a valid course category"
    );
    if (!appliedFilter.courseCategoryId) {
      return;
    }

    if (courseCategories.length < 2) {
      return;
    }

    const a = courseCategories.filter(
      category => category.id === appliedFilter.courseCategoryId
    );

    if (a.length === 0) {
      dispatch(setCategoryFilter(null));
      onUpdateFilter({
        ...appliedFilter,
        courseCategoryId: null
      });
    }
  }, [courseCategories, appliedFilter, dispatch, onUpdateFilter]);

  const isAdminView = myRole === ROLE_SYSTEM_ADMINISTRATOR;

  const showTrainingUnitDetails = id => {
    // Check if user is within iframe for redirections
    let iFrameDetection = window === window.parent ? false : true;

    if (iFrameDetection) {
      console.debug("opening training unit", id);
      window.open(`/course-plan/${id}`, "_blank").focus();
      return;
    } else {
      console.debug("not in iframe, navigating to training unit", id);
      history.push(`/course-plan/${id}`);
    }
  };

  const hideTrainingUnitsDetails = () => history.push("/course-plan");

  const updateLocationFilter = id => {
    let locationId = null;
    if (id) {
      locationId = id;
    }

    dispatch(setLocationFilter(locationId));

    onUpdateFilter({
      ...appliedFilter,
      locationId
    });
  };

  const updateCourseCategoryFilter = id => {
    let courseCategoryId = null;
    if (id) {
      courseCategoryId = id;
    }
    dispatch(setCategoryFilter(courseCategoryId));

    onUpdateFilter({
      ...appliedFilter,
      courseCategoryId
    });
  };

  const updatePeriodFilter = fromString => {
    const parsedFrom = parse(fromString, FORMAT_DATE, new Date());
    const from = isValid(parsedFrom) ? parsedFrom : initialDisplayPeriod.from;
    const to = twoWeeksDown(from);

    const displayPeriod = { from, to };

    setDisplayPeriod(displayPeriod);

    onUpdateFilter({
      ...appliedFilter,
      ...displayPeriod
    });
  };

  const handleRefreshData = () => {
    onRefreshData(displayPeriod);
  };

  const loadMore = () => {
    const newPeriodStart = startOfDay(addDays(displayPeriod.to, 1));
    const newPeriodEnd = twoWeeksDown(newPeriodStart);

    setDisplayPeriod({
      ...displayPeriod,
      to: newPeriodEnd
    });

    onLoadMore({
      from: displayPeriod.to,
      to: newPeriodEnd
    });
  };

  const filterOptions = {
    locations: locations.map(location => ({
      value: location.id,
      text: location.name
    })),
    courseCategories: courseCategories.map(category => ({
      value: category.id,
      text: category.title
    })),
    from: displayPeriod.from
  };

  const trainingUnitsByDate = (trainingUnits, date) => {
    return trainingUnits.filter(trainingUnit => {
      if (
        !isWithinInterval(new Date(trainingUnit.date), {
          start: startOfDay(date),
          end: endOfDay(date)
        })
      ) {
        return false;
      }

      return true;
    });
  };

  return (
    <>
      <List
        trainingUnits={trainingUnits}
        updateLocationFilter={updateLocationFilter}
        updateCourseCategoryFilter={updateCourseCategoryFilter}
        updatePeriodFilter={updatePeriodFilter}
        appliedFilter={appliedFilter}
        filterOptions={filterOptions}
        filterTrainingUnitsByDate={trainingUnitsByDate}
        isLoading={isLoading}
        loadMore={loadMore}
        isAdminView={isAdminView}
        onRefreshData={handleRefreshData}
        displayPeriod={displayPeriod}
        onSelectTrainingUnit={showTrainingUnitDetails}
      />
      <Switch>
        <Route path="/course-plan/:trainingUnitId">
          {({
            match: {
              params: { trainingUnitId }
            }
          }) => (
            <DetailsContainer
              id={trainingUnitId}
              handleClose={hideTrainingUnitsDetails}
              onRefreshData={handleRefreshData}
            />
          )}
        </Route>
      </Switch>
    </>
  );
}

ListContainer.displayName = "coursePlan/ListContainer";

const mapStateToProps = ({ auth: { role } }) => ({
  myRole: role
});

export default connect(mapStateToProps)(React.memo(ListContainer));
